import type {ReactNode} from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import {AuthSession} from './auth-session';
import type {AuthState, PreviewToken} from './auth-session';

export type {AuthState, PreviewToken} from './auth-session';

interface AuthContextValue {
  authState: AuthState;
  identifier: string;
  isPreview: boolean;
  // A public page issued the current session to a visitor with no verified
  // identity, so nothing that needs a grant (e.g. the pages panel) applies.
  isAnonymous: boolean;
  handleVerified: (jwt: string, organizationIdentifier: string) => void;
  reauthenticate: (deniedJwt?: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const useOrganizationIdentifier = (): string | null => {
  const {authState} = useAuth();
  return authState.status === 'authenticated'
    ? authState.organizationIdentifier
    : null;
};

export const useFeatureFlagsReady = (): boolean => {
  const {authState} = useAuth();
  return authState.status !== 'authenticated' || authState.flagsReady;
};

interface AuthProviderProps {
  identifier: string;
  previewToken?: PreviewToken | null;
  children: ReactNode;
}

export const AuthProvider = ({
  identifier,
  previewToken,
  children,
}: AuthProviderProps) => {
  const [session] = useState(() => new AuthSession({identifier, previewToken}));
  const authState = useSyncExternalStore(
    session.subscribe,
    session.getSnapshot,
  );

  useEffect(() => {
    session.start();
    return () => session.dispose();
  }, [session]);

  const isAnonymous =
    authState.status === 'authenticated' && authState.isAnonymous;

  const value = useMemo<AuthContextValue>(
    () => ({
      authState,
      identifier,
      isPreview: session.isPreview,
      isAnonymous,
      handleVerified: session.handleVerified,
      reauthenticate: session.reauthenticate,
    }),
    [authState, identifier, isAnonymous, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
